/**
 * Motion for the living screens (docs/decisions/2026-10-13-panel-living-redesign.md). The Panel depends on the `motion`
 * library through this provider and nowhere else: `LazyMotion` with the `m` components, so a screen ships the small
 * feature set (animate, exit, gestures) and the larger one (layout, shared `layoutId`, drag) only where it asks.
 *
 * Rules: transform and opacity only, springs instead of timed curves, nothing runs while nothing changes. Under
 * reduced motion, or when the page asks to stand still (`data-vv-still`), every change lands in its final state at once.
 */

import { LazyMotion, MotionConfig, MotionGlobalConfig } from 'motion/react';
import { useInsertionEffect } from 'react';
import type { ReactNode } from 'react';

import { useReducedMotion } from '../motion';

/** The three springs of the redesign. The stylesheet has the same three as easings: `--sp-snap`, `--sp-pop`, `--sp-soft`. */
export const SPRING = {
  /** A press, a thumb that slides, a digit that rolls. */
  snap: { type: 'spring', visualDuration: 0.26, bounce: 0.14 },
  /** Something that arrives: a dialog card, a toast, a badge. */
  pop: { type: 'spring', visualDuration: 0.4, bounce: 0.34 },
  /** Something large that settles: a sheet, a layout change. */
  soft: { type: 'spring', visualDuration: 0.52, bounce: 0.1 },
} as const;



/**
 * The small feature set loads apart from the Panel's entry, from the moment the Panel starts. A provider drawn before it
 * lands (Acceso's door at boot) draws its pieces in their first frame and starts their animations when it lands; every
 * provider drawn after it (every screen) receives it synchronously, as when it was in the entry.
 */
type AnimationFeatures = Awaited<ReturnType<typeof importAnimationFeatures>>;
const importAnimationFeatures = () => import('./motion-features-animation').then((module) => module.default);
let animationFeatures: AnimationFeatures | null = null;
let animationFeaturesLoad: Promise<AnimationFeatures> | null = null;
const loadAnimationFeatures = (): Promise<AnimationFeatures> => {
  animationFeaturesLoad ??= importAnimationFeatures().then(
    (loaded) => (animationFeatures = loaded),
    (error: unknown) => { animationFeaturesLoad = null; throw error; },
  );
  return animationFeaturesLoad;
};
void loadAnimationFeatures().catch(() => undefined);

/**
 * The larger set, loaded where a piece asks. Once it landed every provider receives it synchronously: an `m` piece drawn
 * before its features has no exit to finish, so an `AnimatePresence` around it would hold it on screen.
 */
type LayoutFeatures = Awaited<ReturnType<typeof importLayoutFeatures>>;
const importLayoutFeatures = () => import('./motion-features-layout').then((module) => module.default);
let layoutFeatures: LayoutFeatures | null = null;
const loadLayoutFeatures = (): Promise<LayoutFeatures> => importLayoutFeatures().then((loaded) => (layoutFeatures = loaded));

export interface MotionProviderProps {
  /**
   * `animation` (default): animate, exit, hover and tap. `layout`: adds `layout`, `layoutId` and drag, in a lazy chunk. A
   * screen whose pieces leave at a tap from its first frame passes the set itself (`./motion-features-layout`, imported
   * by the screen's chunk).
   */
  readonly features?: 'animation' | 'layout' | LayoutFeatures;
  readonly children: ReactNode;
}

/** One per screen root. `m` components outside it do not animate. */
export function MotionProvider({ features = 'animation', children }: MotionProviderProps) {
  const still = useReducedMotion();
  // Before any child starts an animation: still means every animation completes at once, opacity included.
  useInsertionEffect(() => { MotionGlobalConfig.skipAnimations = still; }, [still]);
  return (
    <LazyMotion features={features === 'animation' ? animationFeatures ?? loadAnimationFeatures : features === 'layout' ? layoutFeatures ?? loadLayoutFeatures : features} strict>
      <MotionConfig reducedMotion={still ? 'always' : 'never'} transition={SPRING.soft}>{children}</MotionConfig>
    </LazyMotion>
  );
}

/** True when nothing may move: reduced motion, or the page asked to stand still. */
export const useLivingStill = useReducedMotion;
